import { ImageArea, ImageCropResult } from 'models';

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getRadianAngle = (degreeValue: number): number => {
  return (degreeValue * Math.PI) / 180;
};

const cropImage = async (
  imageSrc: string,
  pixelCrop: ImageArea,
  rotation = 0,
): Promise<ImageCropResult | null> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const resultImage = null;

  if (ctx) {
    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    // Set each dimensions to double largest dimension to allow for a safe area
    // for the image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    // Translate canvas context to a central location on image to allow
    // rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // Draw rotated image and store data.
    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5,
    );
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // Set canvas width to final desired crop size.
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
      0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y,
    );

    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        if (file) {
          resolve({ url: canvas.toDataURL('image/jpeg'), file });
        }
      }, 'image/jpeg');
    });
  }
  return resultImage;
};

const resizeImage = async (
  imageSrc: string,
): Promise<ImageCropResult | null> => {
  const WIDTH = 1125;
  const HEIGHT = 2000;
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const resultImage = null;

  if (ctx && image.width > WIDTH) {
    canvas.width = WIDTH;
    canvas.height = HEIGHT;

    ctx.drawImage(image, 0, 0, WIDTH, HEIGHT);

    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        if (file) {
          resolve({ url: canvas.toDataURL('image/jpeg'), file });
        }
      }, 'image/jpeg');
    });
  }
  return resultImage;
};

const getResizedImage = async (
  imageSrc: string,
  pixelCrop: ImageArea,
): Promise<ImageCropResult | null> => {
  let result = null;
  const cropedImage = await cropImage(imageSrc, pixelCrop);
  if (cropedImage) {
    const resizedImage = await resizeImage(cropedImage?.url);
    result = resizedImage || cropedImage;
  }
  return result;
};

export default getResizedImage;
